// src/components/Game.js
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { db } from '../firebase';
import { doc, updateDoc, increment, getDoc, setDoc } from 'firebase/firestore';

export default function Game({ team, teamScores }) {
    const { userData, updateUserData, reloadUserData } = useAuth();
    const { t } = useLanguage();

    const [isPlaying, setIsPlaying] = useState(false);
    const [showResult, setShowResult] = useState('');
    const gameRef = useRef(null);
    const verticalBallRef = useRef(null);
    const horizontalBallRef = useRef(null);
    const animationRef = useRef(null);
    const clickCountRef = useRef(0);
    const verticalStoppedRef = useRef(false);
    const horizontalStoppedRef = useRef(false);

    // Calculate ball speed based on team lead
    const ballSpeed = useCallback(() => {
        if (!team || !teamScores){
            console.log("team and teamScores are undefined");
            return 1.5;
        }

        const diff = Math.abs(teamScores.blackWhite - teamScores.redWhite);
        const leadingTeam = teamScores.blackWhite > teamScores.redWhite ? 'blackWhite' : 'redWhite';

        let speed = 1.5;
        if (team === leadingTeam && diff > 0) {
            speed = 1.5 + (diff * 0.01);
        }
        console.log("speed: " + speed);
        return speed;
    }, [team, teamScores]);

    const checkAndResetDaily = async () => {
        const today = new Date().toDateString();
        if (userData.lastPlayDate !== today) {
            await reloadUserData(); // Ovo poziva loadUserData
        }
    };

    const startGame = useCallback(async () => {
        await checkAndResetDaily();
        const totalAttempts = (userData?.dailyAttempts || 0) + (userData?.bonusAttempts || 0);

        if (totalAttempts <= 0) {
            alert(t('noAttempts'));
            return;
        }

        setIsPlaying(true);
        setShowResult('');
        clickCountRef.current = 0;
        verticalStoppedRef.current = false;
        horizontalStoppedRef.current = false;

        animateBalls();
    }, [userData, t]);

    const animateBalls = useCallback(() => {
        const speed = ballSpeed();
        const animate = () => {
            if (!verticalBallRef.current || !horizontalBallRef.current) return;
            const time = Date.now() / 1000;
            if (!verticalStoppedRef.current) {
                const y = Math.sin(time * speed) * 130;
                verticalBallRef.current.style.transform = `translateY(${y}px)`;
            }
            if (!horizontalStoppedRef.current) {
                const x = Math.cos(time * speed) * 130;
                horizontalBallRef.current.style.transform = `translateX(${x}px)`;
            }
            if (!verticalStoppedRef.current || !horizontalStoppedRef.current) {
                animationRef.current = requestAnimationFrame(animate);
            }
        };
        animate();
    }, [ballSpeed]);

    const handleGameClick = async () => {
        if (!isPlaying) return;

        clickCountRef.current++;

        if (clickCountRef.current === 1) {
            verticalStoppedRef.current = true;
        } else if (clickCountRef.current === 2) {
            horizontalStoppedRef.current = true;
            setIsPlaying(false);

            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }

            // Check result
            const verticalY = parseFloat(verticalBallRef.current.style.transform.match(/translateY\(([-\d.]+)px\)/)?.[1] || 0);
            const horizontalX = parseFloat(horizontalBallRef.current.style.transform.match(/translateX\(([-\d.]+)px\)/)?.[1] || 0);

            const distance = Math.sqrt(verticalY * verticalY + horizontalX * horizontalX);
            const isHit = distance < 20;

            // Update attempts
            try {
                if (isHit) {
                    if (userData.dailyAttempts > 0) {
                        await updateUserData({
                            dailyAttempts: userData.dailyAttempts - 1,
                            totalScore: (userData.totalScore || 0) + 1,
                            dailyScore: (userData.dailyScore || 0) + 1
                        });
                    } else {
                        await updateUserData({
                            bonusAttempts: userData.bonusAttempts - 1,
                            totalScore: (userData.totalScore || 0) + 1,
                            dailyScore: (userData.dailyScore || 0) + 1
                        });
                    }

                    // Update team score
                    const today = new Date().toDateString();
                    const dailyScoreRef = doc(db, 'dailyScores', today);
                    const dailyScoreDoc = await getDoc(dailyScoreRef);

                    if (dailyScoreDoc.exists()) {
                        await updateDoc(dailyScoreRef, {
                            [team]: increment(1)
                        });
                    } else {
                        await setDoc(dailyScoreRef, {
                            blackWhite: team === 'blackWhite' ? 1 : 0,
                            redWhite: team === 'redWhite' ? 1 : 0
                        });
                    }

                    setShowResult('hit');
                } else {
                    if (userData.dailyAttempts > 0) {
                        await updateUserData({
                            dailyAttempts: userData.dailyAttempts - 1
                        });
                    } else {
                        await updateUserData({
                            bonusAttempts: userData.bonusAttempts - 1
                        });
                    }
                    setShowResult('miss');
                }
            } catch (error) {
                console.error("Error updating game state:", error);
            }

            // Reset for next game
            setTimeout(() => {
                setShowResult('');
                const totalAttempts = (userData?.dailyAttempts || 0) + (userData?.bonusAttempts || 0) - 1;
                if (totalAttempts > 0) {
                    startGame();
                }
            }, 1000);
        }
    };

    // Cleanup animation on unmount
    useEffect(() => {
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            // …your “setup” logic here
            if (animationRef.current && isPlaying === false) {
                cancelAnimationFrame(animationRef.current);
            }
        }, 200);

        return () => {
            clearTimeout(timer);
        };
    }, [isPlaying]);

    const totalAttempts = (userData?.dailyAttempts || 0) + (userData?.bonusAttempts || 0);

    return (
        <div className="relative">
            <div className="mt-4 text-center p-4 flex justify-center gap-4">
                <button
                    onClick={startGame}
                    className="px-6 py-3 bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed"
                    disabled={totalAttempts <= 0 || isPlaying}
                >
                    {t('play')} ({totalAttempts} {t('attempts').toLowerCase()})
                </button>
                {isPlaying && (
                    <button
                        onClick={() => {
                            setIsPlaying(false)
                        }}
                        className="px-6 py-3 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        {t('pause')}
                    </button>
                )}
            </div>
            <div
                ref={gameRef}
                onClick={handleGameClick}
                className="relative bg-gray-700 rounded-lg p-8 cursor-pointer select-none overflow-hidden"
                style={{ height: '400px' }}
            >
                <p className="text-center mb-4 text-gray-300">
                    {t('clickToStop')}
                </p>

                {/* Plus sign */}
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                        {/* Vertical line */}
                        <div className="absolute w-1 h-64 bg-white left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"></div>
                        {/* Horizontal line */}
                        <div className="absolute h-1 w-64 bg-white left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"></div>

                        {/* Vertical ball */}
                        <div
                            ref={verticalBallRef}
                            className="absolute w-8 h-8 bg-yellow-400 rounded-full left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 shadow-lg"
                            style={{ marginLeft: '0px' }}
                        ></div>

                        {/* Horizontal ball */}
                        <div
                            ref={horizontalBallRef}
                            className="absolute w-8 h-8 bg-blue-400 rounded-full left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 shadow-lg"
                            style={{ marginTop: '0px' }}
                        ></div>
                    </div>
                </div>

                {/* Result display */}
                {showResult && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className={`text-4xl font-bold ${showResult === 'hit' ? 'text-green-400' : 'text-red-400'} 
                          bg-gray-900 bg-opacity-90 px-6 py-3 rounded-lg`}>
                            {t(showResult)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}